import React from "react";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { formatDateTime } from "../../shared/utils/format";
import { useUserSection } from "./useUserSection";
import { LoadState, Muted, SectionCard, StatusBadge } from "./ui";
import type { BrandKitEntry, BrandKitSummary } from "./types";

const label = (value: string | null) => (value ? value.replaceAll("_", " ") : "—");

const Chips: React.FC<{ items: string[] }> = ({ items }) =>
  items.length === 0 ? (
    <span className="text-sm text-gray-400">—</span>
  ) : (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span key={item} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">
          {item}
        </span>
      ))}
    </div>
  );

const Bullets: React.FC<{ items: string[] }> = ({ items }) =>
  items.length === 0 ? (
    <span className="text-sm text-gray-400">—</span>
  ) : (
    <ul className="list-disc space-y-1 pl-4 text-sm text-gray-800">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );

const Block: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
    {children}
  </div>
);

const Fact: React.FC<{ name: string; value: React.ReactNode }> = ({ name, value }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <span className="shrink-0 text-gray-500">{name}</span>
    <span className="text-right font-medium capitalize text-gray-900">{value}</span>
  </div>
);

function KitQuality({ kit }: Readonly<{ kit: BrandKitSummary }>) {
  const { quality } = kit;
  return (
    <Block title="Generation quality">
      <Fact name="Source" value={label(quality.sourceKind)} />
      <Fact name="Crawl quality" value={quality.crawlQuality ? <StatusBadge status={quality.crawlQuality} /> : "—"} />
      <Fact name="Profile score" value={quality.profileScore === null ? "—" : `${Math.round(quality.profileScore * 100)}%`} />
      {quality.recoverySource && <Fact name="Recovered via" value={label(quality.recoverySource)} />}
      {quality.issues.length > 0 && (
        <div className="mt-1.5">
          <Chips items={quality.issues.map((i) => i.replaceAll("_", " "))} />
        </div>
      )}
    </Block>
  );
}

function KitDetails({ kit }: Readonly<{ kit: BrandKitSummary }>) {
  const { profile, voice, facts } = kit;
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            {kit.logoUrl ? (
              <img src={kit.logoUrl} alt="" className="h-14 w-14 rounded-lg border border-gray-100 bg-white object-contain p-1" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-gray-100 text-xs text-gray-400">No logo</div>
            )}
            <div className="min-w-0">
              <p className="truncate font-semibold text-gray-900">{kit.name ?? "—"}</p>
              {kit.website && (
                <a
                  href={kit.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-violet-600 hover:underline"
                >
                  {kit.website.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
          <div>
            <Fact name="Industry" value={profile.industry ?? "—"} />
            <Fact name="Business type" value={label(profile.businessType)} />
            <Fact name="Archetype" value={label(kit.archetype)} />
            <Fact name="Price range" value={label(profile.priceRange)} />
            <Fact name="Markets" value={profile.markets ?? "—"} />
            <Fact name="Language" value={kit.language ?? "—"} />
          </div>
        </div>

        <div className="space-y-4">
          <Block title="Colors">
            {kit.colors.length === 0 ? (
              <span className="text-sm text-gray-400">—</span>
            ) : (
              <div className="flex flex-wrap gap-3">
                {kit.colors.map((c) => (
                  <div key={`${c.hex}-${c.role}`} className="flex items-center gap-2">
                    <span className="h-8 w-8 rounded-md border border-gray-200" style={{ backgroundColor: c.hex }} />
                    <span className="text-xs">
                      <span className="block font-mono text-gray-900">{c.hex}</span>
                      <Muted>{c.role ?? ""}</Muted>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Block>
          <Block title="Fonts">
            {kit.fonts.length === 0 ? (
              <span className="text-sm text-gray-400">—</span>
            ) : (
              kit.fonts.map((f) => (
                <Fact key={`${f.role}-${f.family}`} name={label(f.role)} value={[f.family, f.weight].filter(Boolean).join(" ")} />
              ))
            )}
          </Block>
          {(kit.mood || kit.style) && (
            <Block title="Visual style">
              <p className="text-sm text-gray-800">{[kit.style, kit.mood].filter(Boolean).join(" · ")}</p>
            </Block>
          )}
        </div>

        <KitQuality kit={kit} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <Block title="USP">
            <p className="text-sm text-gray-800">{profile.usp ?? "—"}</p>
          </Block>
          <Block title={voice.name ? `Voice · ${voice.name}` : "Voice"}>
            <Chips items={[...voice.tone, ...voice.character]} />
          </Block>
          <Block title="Audience">
            <Chips items={voice.audience} />
            <p className="mt-1.5 text-xs text-gray-500">
              Age {profile.audienceAge ?? "—"} · Gender {label(profile.audienceGender)}
            </p>
          </Block>
          <Block title="Pain points">
            <Bullets items={profile.painPoints} />
          </Block>
          <Block title="Desires">
            <Bullets items={profile.desires} />
          </Block>
        </div>
        <div className="space-y-4">
          <Block title="Marketing goal">
            <p className="text-sm text-gray-800">{profile.marketingGoal ?? "—"}</p>
          </Block>
          <Block title="Core services">
            <Chips items={[...facts.coreServices, ...facts.offerings]} />
          </Block>
          <Block title="Differentiators">
            <Bullets items={facts.differentiators} />
          </Block>
          <Block title="Buyer personas">
            <Bullets items={facts.buyerPersonas} />
          </Block>
          <Block title="Keywords">
            <Chips items={profile.keywords} />
          </Block>
        </div>
      </div>

      {profile.summary && (
        <details className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">Full brand profile</summary>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{profile.summary}</p>
        </details>
      )}
    </div>
  );
}

function incompleteReason(kit: BrandKitSummary): string {
  const parent = kit.parentBrand;
  if (parent && parent.status !== "successful") {
    return `Waiting on the parent brand ${parent.name} — its brand kit import is ${parent.status}.`;
  }
  return "Incomplete brand kit — ingestion did not produce a brand profile or visual kit.";
}

function KitCard({ entry }: Readonly<{ entry: BrandKitEntry }>) {
  const { kit } = entry;
  const title = entry.businessName || `Brand #${entry.clientId}`;
  if (!kit) {
    return (
      <SectionCard title={title}>
        <p className="py-6 text-center text-sm text-gray-400">No brand kit generated yet.</p>
      </SectionCard>
    );
  }
  return (
    <SectionCard
      title={title}
      action={
        <span className="flex flex-wrap items-center gap-2">
          {kit.editedByUser && <StatusBadge status="edited by user" />}
          <Muted>
            v{kit.version} · updated {formatDateTime(kit.updatedAt)}
          </Muted>
        </span>
      }
    >
      {kit.parentBrand && (
        <p className="mb-4 text-sm text-gray-600">
          Franchise location — shared brand identity from parent brand{" "}
          <span className="font-medium text-gray-800">{kit.parentBrand.name}</span>.
        </p>
      )}
      {kit.complete ? (
        <KitDetails kit={kit} />
      ) : (
        <div className="space-y-4">
          <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {incompleteReason(kit)}
          </p>
          <KitQuality kit={kit} />
        </div>
      )}
    </SectionCard>
  );
}

export const BrandKitTab: React.FC<{ userId: number; clientId?: number }> = ({ userId, clientId }) => {
  const { data, loading, error, reload } = useUserSection<{ items: BrandKitEntry[] }>(userId, "brand-kits", { clientId });

  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200">
        <LoadState loading={loading} error={error} onRetry={reload} />
      </div>
    );
  }
  if (data.items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-sm text-gray-400">
        This user has no brands yet.
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {data.items.map((entry) => (
        <KitCard key={entry.clientId} entry={entry} />
      ))}
    </div>
  );
};
